import { Link } from "react-router-dom";
import { useEffect } from "react";
import { ArrowRight, PenLine, Feather, Mail, MailOpen, Users, Gift, Sparkles } from "lucide-react";
import Testimonials from "../components/Testimonials";
import Reveal from "../components/Reveal";
import { FEATURE_ACCOMPAGNEMENT, FEATURE_CORRESPONDANCE } from "../config/features";

const IMG_HERO = "/hero-desk.jpg?v=1";
const IMG_AMELIE = "https://images.unsplash.com/photo-1603950227760-e609ce8e15b4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwzfHxjdXAlMjBvZiUyMHRlYSUyMGFuZCUyMG9wZW4lMjBib29rJTIwbWluaW1hbGlzdHxlbnwwfHx8fDE3ODkyMDYxMTV8MA&ixlib=rb-4.1.0&q=85";
const IMG_GIFT = "https://images.unsplash.com/photo-1604304194650-3ba3cfa752fd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwyfHxkcmllZCUyMGZsb3dlcnMlMjBlbnZlbG9wZSUyMGNyYWZ0JTIwcGFwZXJ8ZW58MHx8fHwxNzg5MjA2MTE1fDA&ixlib=rb-4.1.0&q=85";

// ── Section 4 : Les portes d'entrée (offres réelles du site) ────────────────
const doorwaysAll = [
  {
    icon: MailOpen,
    tag: "le Club de Lettres",
    title: "Le Club de Lettres",
    body: "Chaque mois, une enveloppe dans ta boîte aux lettres : une enveloppe remplie de douceurs et de petites attentions, au fil des thèmes du mois.",
    hint: "Dès 12€/mois · sans engagement · formules 3, 6 et 12 mois",
    to: "/club-de-lettres",
    cta: "Je découvre le Club de Lettres",
    testid: "door-mailclub",
    _kind: "mailclub",
  },
  {
    icon: PenLine,
    tag: "les programmes",
    title: "Les 52 rendez-vous d'écriture",
    body: "Chaque semaine, une invitation à écrire glissée dans ta boîte mail. Une thématique douce, une question, un prétexte à ralentir.",
    hint: "À partir de 102€ · paiement unique",
    to: "/programme",
    cta: "Je découvre le programme",
    testid: "door-programme",
    _kind: "programme",
  },
  {
    icon: Mail,
    tag: "correspondance",
    title: "Une vraie lettre entre nous",
    body: "Tu écris. Je te réponds à la main, sur du papier choisi. Une correspondance manuscrite, lente et personnelle.",
    hint: "À partir de 35€",
    to: "/correspondance",
    cta: "J'entre dans la correspondance",
    testid: "door-correspondance",
    _kind: "correspondance",
  },
  {
    icon: Users,
    tag: "ateliers",
    title: "Des ateliers d'écriture",
    body: "Des rendez-vous collectifs pour écrire ensemble, autour d'une thématique, dans une atmosphère douce et bienveillante.",
    hint: "En ligne et en présentiel",
    to: "/ateliers",
    cta: "Je découvre les ateliers",
    testid: "door-ateliers",
    _kind: "ateliers",
  },
  {
    icon: Feather,
    tag: "accompagnement",
    title: "Un accompagnement personnalisé",
    body: "Un discours, un hommage, une lettre importante… Tu as les mots quelque part, je t'aide à les trouver.",
    hint: "À partir de 110€",
    to: "/accompagnement",
    cta: "Je veux trouver mes mots",
    testid: "door-accompagnement",
    _kind: "accompagnement",
  },
  {
    icon: Gift,
    tag: "cartes cadeaux",
    title: "Offrir un rendez-vous avec les mots",
    body: "Un anniversaire, une naissance, Noël, ou simplement une attention — offrir du temps, une expérience qui prend son temps.",
    hint: "À partir de 12€",
    to: "/carte-cadeau",
    cta: "J'offre un rendez-vous",
    testid: "door-gift",
    _kind: "gift",
  },
];
const doorways = doorwaysAll.filter(d => (d._kind !== "accompagnement" || FEATURE_ACCOMPAGNEMENT) && (d._kind !== "correspondance" || FEATURE_CORRESPONDANCE));

// ── Section 5 : Les objections / reconnaissance ────────────────────────────
const belongings = [
  "tu as toujours aimé écrire, mais tu as laissé cette envie de côté",
  "tu as un joli carnet, quelque part, jamais vraiment commencé",
  "tu aimerais écrire davantage, mais tu ne sais jamais quoi raconter",
  "tu attends « d'avoir le temps », une meilleure période, le bon moment",
  "tu ne te considères pas comme quelqu'un qui écrit",
  "tu aimerais retrouver une pratique créative, un peu à toi",
  "tu aimes les carnets, les lettres, le papier, les choses qui prennent le temps",
  "tu aimerais recevoir autre chose qu'une notification sur ton téléphone",
];

// ── Section 6 : Ce que tu vas vraiment vivre ───────────────────────────────
const experiences = [
  { title: "Un rendez-vous, chaque semaine", body: "Une invitation dans ta boîte mail, un petit signe régulier qui te rappelle que c'est ton moment." },
  { title: "Une thématique douce", body: "Un souvenir, une odeur, une saison, une émotion, une personne. Jamais rien d'imposé — juste une porte à pousser si tu en as envie." },
  { title: "Une question pour t'aider à démarrer", body: "Pas de page blanche : chaque rendez-vous propose une phrase-guide, une question, une amorce. Tu n'as qu'à ouvrir ton carnet." },
  { title: "Ton rythme, jamais le mien", body: "Tu écris quand tu veux, où tu veux, autant que tu veux. Tu peux tout garder pour toi, ou en partager une bribe — rien n'est jamais évalué." },
  { title: "Une trace qui reste", body: "Au fil des semaines, ton carnet se remplit. Tu vois évoluer ce que tu déposes, ce que tu explores, ce qui revient." },
  { title: "Une pratique qui s'installe", body: "Petit à petit, écrire redevient naturel. Une habitude douce, un espace personnel qui prend sa place dans ta vie." },
];

// ── Section 9 : Réassurance ────────────────────────────────────────────────
const reassurances = [
  { q: "Faut-il savoir écrire pour participer ?", a: "Non. Il faut simplement avoir envie d'écrire. Tu n'as pas besoin d'être écrivain, ni d'avoir une jolie plume." },
  { q: "Puis-je commencer quand je veux ?", a: "Oui. Tu reçois ton premier rendez-vous dès que tu t'inscris, puis un nouveau chaque semaine — à ton rythme." },
  { q: "Comment vais-je recevoir le programme ?", a: "Directement dans ta boîte mail, chaque semaine, un lundi matin. Rien à installer, rien à télécharger." },
  { q: "Puis-je offrir un programme ?", a: "Oui. Une carte cadeau permet d'offrir un programme, une correspondance, ou le Club de Lettres pour 3, 6 ou 12 mois (France et international)." },
  { q: "Et si je veux arrêter le Club de Lettres ?", a: "Le Club de Lettres est sans engagement : tu peux l'arrêter depuis ton espace personnel, en un clic, à tout moment." },
];

export default function Home() {
  useEffect(() => {
    const title = FEATURE_ACCOMPAGNEMENT
      ? "Les Rendez-vous de l'écriture — programmes d'écriture, correspondance manuscrite & accompagnement"
      : "Les Rendez-vous de l'écriture — programmes d'écriture & correspondance manuscrite";
    const desc = FEATURE_ACCOMPAGNEMENT
      ? "Les Rendez-vous de l'écriture : programmes d'écriture par email, correspondance manuscrite et accompagnement pour trouver les mots (discours de mariage, hommage, lettre personnelle). L'écriture, en toute tendresse."
      : "Les Rendez-vous de l'écriture : programmes d'écriture par email, correspondance manuscrite, ateliers et Club de Lettres. Un moment doux à toi. L'écriture, en toute tendresse.";
    document.title = title;
    const setMeta = (attr, name, content) => {
      let el = document.head.querySelector(`meta[${attr}="${name}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute(attr, name); document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    setMeta("name", "description", desc);
    setMeta("property", "og:title", title);
    setMeta("property", "og:url", "https://lesrdvdelecriture.com/");
  }, []);

  return (
    <div data-testid="home-page">
      {/* H1 sémantique invisible — pour SEO exact-match */}
      <h1 className="sr-only">Les Rendez-vous de l'écriture — programmes d'écriture, correspondance manuscrite, Club de Lettres et ateliers</h1>

      {/* ── 1. HERO ─────────────────────────────────────────────────────── */}
      <section className="paper-grain bg-[#F5F0E6] border-b border-[#E8DCC8]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 pt-12 md:pt-20 pb-16 md:pb-24 grid md:grid-cols-12 gap-10 md:gap-6 items-center fade-in">
        <div className="md:col-span-7 order-2 md:order-1">
          <p className="font-script text-5xl sm:text-6xl lg:text-7xl text-[#C35A3E] leading-none -rotate-2 origin-left mb-4">Le Club de Lettres</p>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl text-[#2C2621] leading-[1.05] mb-5 md:mb-6">
            Offre-toi une <em className="italic text-[#A3876A]">aventure épistolaire</em> unique.
          </h2>
          <p className="text-base md:text-lg text-[#4A4036] leading-relaxed mb-4 max-w-xl">
            Rendez-vous chaque mois dans ta boîte aux lettres : une enveloppe remplie de douceurs et de petites attentions — une lettre, une inspiration de la nature, une carte à garder, une invitation à écrire (et à me répondre)… et une petite surprise.
          </p>
          <p className="text-base md:text-lg text-[#4A4036] leading-relaxed mb-8 max-w-xl italic">
            Juste l'envie de recevoir autre chose qu'une facture dans sa boîte aux lettres.
          </p>
          <p data-testid="hero-price-hint" className="sticker sticker-cream -rotate-1 font-serif text-xl md:text-2xl text-[#2C2621] mb-6">Dès 12 € par mois <span className="text-sm md:text-base text-[#7C7064]">· sans engagement · frais de port inclus</span></p>
          <div className="flex flex-wrap items-center gap-4">
            <Link to="/club-de-lettres" data-testid="cta-hero-club" className="inline-flex items-center gap-2 px-8 py-4 bg-[#C35A3E] text-[#FDFBF7] hover:bg-[#A94A31] transition-colors duration-300 shadow-[0_12px_24px_-12px_rgba(195,90,62,.7)]">
              Rejoindre le Club de Lettres <ArrowRight size={14}/>
            </Link>
          </div>
          <p className="text-sm text-[#7C7064] mt-5 max-w-xl">
            Et pour aller plus loin : des <Link to="/programme" className="underline underline-offset-2 hover:text-[#A3876A]">programmes d'écriture</Link>{FEATURE_CORRESPONDANCE && <>, une <Link to="/correspondance" className="underline underline-offset-2 hover:text-[#A3876A]">correspondance manuscrite</Link></>} et des <Link to="/ateliers" className="underline underline-offset-2 hover:text-[#A3876A]">ateliers</Link>.
          </p>
          <p className="font-hand text-lg md:text-xl text-[#A3876A] italic mt-8">L'écriture, en toute tendresse.</p>
        </div>
        <div className="md:col-span-5 order-1 md:order-2 relative">
          <div className="photo-print rotate-2 md:rotate-3">
            <img src="/club-lettre-main.jpg" alt="Amélie écrit à la main la lettre du Club de Lettres, entourée de cartes et de stickers" className="w-full h-[320px] sm:h-[420px] md:h-[540px] object-cover object-center"/>
          </div>
          <span className="sticker sticker-terra absolute -left-3 bottom-10 -rotate-6 font-hand text-xl">Écrite à la main, chaque mois</span>
          <div className="absolute -right-3 -top-3 md:-right-5 md:-top-5"><div className="wax-seal"><Mail size={20}/></div></div>
        </div>
      </div>
      </section>

      {/* ── 2. COMPRÉHENSION IMMÉDIATE ─────────────────────────────────── */}
      <Reveal as="section" className="bg-[#F5F0E6] py-16 md:py-24 border-y border-[#E8DCC8]">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <p className="font-hand text-xl md:text-2xl text-[#A3876A] mb-4">Les RDV de l'écriture</p>
          <h2 className="font-serif text-2xl md:text-4xl lg:text-5xl text-[#2C2621] leading-tight mb-6 md:mb-8">
            Un univers doux où l'on écrit <em className="italic">sans avoir à être écrivain</em>.
          </h2>
          <p className="text-base md:text-lg text-[#4A4036] leading-relaxed">
            Un Club de Lettres mensuel par la poste, des programmes hebdomadaires, une correspondance manuscrite et des ateliers collectifs.
            Différentes portes vers une même idée&nbsp;: <em className="italic">redonner une place aux mots dans ton quotidien</em>.
          </p>
        </div>
      </Reveal>

      {/* ── 3. PRÉSENTATION DES OFFRES ─────────────────────────────────── */}
      <Reveal as="section" id="offres" className="max-w-6xl mx-auto px-6 md:px-12 py-16 md:py-24 scroll-mt-20">
        <div className="max-w-2xl mb-10 md:mb-16">
          <p className="font-hand text-xl md:text-2xl text-[#A3876A] mb-3">Quelle porte as-tu envie de pousser ?</p>
          <h2 className="font-serif text-2xl md:text-4xl text-[#2C2621] leading-tight">
            Plusieurs façons de vivre <em className="italic">l'écriture</em>.
          </h2>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {doorways.map((d) => {
            const Icon = d.icon;
            return (
              <article
                key={d.testid}
                data-testid={d.testid}
                className="bg-[#FDFBF7] p-7 md:p-8 border border-[#E8DCC8] flex flex-col group hover:border-[#A3876A] transition-colors"
              >
                <Icon size={26} className="text-[#A3876A] mb-4" strokeWidth={1.2}/>
                <p className="font-hand text-base text-[#A3876A] mb-2">{d.tag}</p>
                <h3 className="font-serif text-xl md:text-2xl text-[#2C2621] mb-3 leading-tight">{d.title}</h3>
                <p className="text-[#4A4036] leading-relaxed mb-4 flex-1 text-sm md:text-base">{d.body}</p>
                <p className="text-sm text-[#7C7064] mb-5 italic">{d.hint}</p>
                <Link to={d.to} data-testid={`${d.testid}-cta`} className="text-[#A3876A] hover:text-[#8A7158] font-serif italic text-base inline-flex items-center gap-2 underline underline-offset-4 decoration-[#DCCBBA] hover:decoration-[#A3876A]">
                  {d.cta} <ArrowRight size={14}/>
                </Link>
              </article>
            );
          })}
        </div>
      </Reveal>

      {/* ── 4. FOCUS 52 RENDEZ-VOUS (avec prix) ─────────────────────────── */}
      <Reveal as="section" className="bg-[#F5F0E6] py-16 md:py-24 border-y border-[#E8DCC8]">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-12 gap-8 md:gap-12 items-center">
            <div className="md:col-span-7">
              <p className="font-hand text-xl md:text-2xl text-[#A3876A] mb-3">Le programme phare</p>
              <h2 className="font-serif text-3xl md:text-5xl text-[#2C2621] leading-tight mb-6">
                Les 52 rendez-vous <em className="italic">d'écriture</em>.
              </h2>
              <p className="text-lg md:text-2xl text-[#4A4036] font-serif italic leading-relaxed mb-6">
                1 an.<br/>52 thématiques.<br/>52 rendez-vous.
              </p>
              <p className="text-base md:text-lg text-[#4A4036] leading-relaxed mb-8">
                Une invitation à écrire chaque semaine, glissée dans ta boîte mail. À ton rythme, sans jamais rien d'obligé, comme un petit rendez-vous que l'on attend.
              </p>
              <div className="flex flex-col sm:flex-row sm:items-baseline gap-3 sm:gap-5 mb-7">
                <span className="font-serif text-3xl md:text-4xl text-[#2C2621]">À partir de 102€</span>
                <span className="text-sm text-[#7C7064] italic">Paiement unique · sans abonnement</span>
              </div>
              <Link to="/programme" data-testid="cta-focus-programme" className="btn-primary">
                Découvrir le programme <ArrowRight size={14}/>
              </Link>
            </div>
            <div className="md:col-span-5">
              <img src={IMG_HERO} alt="Carnet ouvert, tasse de thé et plume — le programme d'écriture" className="w-full h-[280px] md:h-[420px] object-cover"/>
            </div>
          </div>
        </div>
      </Reveal>

      {/* ── 5. EST-CE QUE C'EST POUR MOI ? ─────────────────────────────── */}
      <Reveal as="section" className="max-w-5xl mx-auto px-6 md:px-12 py-16 md:py-24">
        <div className="max-w-2xl mb-10 md:mb-14">
          <p className="font-hand text-xl md:text-2xl text-[#A3876A] mb-3">Peut-être que tu es au bon endroit si…</p>
          <h2 className="font-serif text-2xl md:text-4xl text-[#2C2621] leading-tight">
            Tu n'as pas besoin de savoir écrire pour <em className="italic">avoir envie d'écrire</em>.
          </h2>
        </div>

        <ul className="grid md:grid-cols-2 gap-4 md:gap-5 mb-12">
          {belongings.map((b, i) => (
            <li key={b} data-testid={`belonging-${i}`} className="flex items-start gap-3 bg-[#FDFBF7] border border-[#E8DCC8] p-5 md:p-6">
              <span className="font-hand text-2xl text-[#A3876A] leading-none mt-0.5" aria-hidden="true">·</span>
              <p className="text-[#4A4036] leading-relaxed text-sm md:text-base italic">{b}</p>
            </li>
          ))}
        </ul>

        <div className="bg-[#F5F0E6] border-l-2 border-[#A3876A] p-6 md:p-8 max-w-3xl">
          <p className="text-base md:text-lg text-[#4A4036] leading-relaxed mb-3">
            Tu n'as pas besoin d'une histoire extraordinaire. Tu peux commencer par une odeur, un souvenir, une personne, une question, une colère, une joie, un paysage, une lettre que tu n'as jamais envoyée.
          </p>
          <p className="text-base md:text-lg text-[#4A4036] leading-relaxed italic">
            Ou simplement quelques mots qui viennent aujourd'hui. Les RDV de l'écriture sont là pour ça — te donner un prétexte, une impulsion, un rendez-vous à attendre.
          </p>
        </div>
      </Reveal>

      {/* ── 6. CE QUE TU VAS VRAIMENT VIVRE ─────────────────────────────── */}
      <Reveal as="section" className="bg-[#FDFBF7] py-16 md:py-24 border-y border-[#E8DCC8]">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="max-w-2xl mb-10 md:mb-14">
            <p className="font-hand text-xl md:text-2xl text-[#A3876A] mb-3">L'expérience</p>
            <h2 className="font-serif text-2xl md:text-4xl text-[#2C2621] leading-tight">
              Ce que tu vas <em className="italic">vraiment vivre</em>.
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {experiences.map((e, i) => (
              <div key={e.title} data-testid={`experience-${i}`} className="border-l border-[#DCCBBA] pl-5 md:pl-6">
                <p className="font-hand text-lg text-[#A3876A] mb-2">0{i + 1}</p>
                <h3 className="font-serif text-lg md:text-xl text-[#2C2621] mb-3 leading-tight">{e.title}</h3>
                <p className="text-[#4A4036] leading-relaxed text-sm md:text-base">{e.body}</p>
              </div>
            ))}
          </div>
        </div>
      </Reveal>

      {/* ── 7. TÉMOIGNAGES ─────────────────────────────────────────────── */}
      <Reveal><Testimonials/></Reveal>

      {/* ── 8. AMÉLIE ─────────────────────────────────────────────────── */}
      <Reveal as="section" className="max-w-6xl mx-auto px-6 md:px-12 py-16 md:py-24 grid md:grid-cols-12 gap-8 md:gap-12 items-start">
        <div className="md:col-span-5 order-1 md:sticky md:top-24">
          <img src={IMG_AMELIE} alt="Un carnet, une tasse de thé — atmosphère du bureau d'Amélie" className="w-full h-[320px] md:h-[520px] object-cover"/>
          <p className="font-hand text-lg text-[#A3876A] mt-4 italic text-center md:text-left">Amélie — fondatrice</p>
        </div>
        <div className="md:col-span-7 order-2">
          <p className="font-hand text-xl md:text-2xl text-[#A3876A] mb-4">Derrière Les RDV de l'écriture</p>
          <h2 className="font-serif text-2xl md:text-4xl lg:text-5xl text-[#2C2621] leading-tight mb-6 md:mb-8">
            Derrière tout ça, <em className="italic">il y a moi</em>.
          </h2>
          <div className="space-y-4 md:space-y-5 text-[#4A4036] leading-relaxed text-base md:text-lg">
            <p>Je m'appelle Amélie.</p>
            <p>Je suis une femme, une maman, une amoureuse des mots et des grands espaces.</p>
            <p>
              J'aime la nature, les chemins qui s'étirent, les endroits où l'on respire un peu plus grand. J'aime les choses simples, les détails que l'on remarque quand on prend le temps, les carnets que l'on ouvre sans savoir exactement ce que l'on va y déposer.
            </p>
            <p className="font-serif italic text-[#2C2621]">Et puis il y a les mots.</p>
            <p>
              Ceux que l'on cherche. Ceux que l'on garde pour soi. Ceux que l'on écrit sur un coin de papier. Ceux que l'on aurait aimé dire. Ceux que l'on envoie dans une lettre et qui prennent, soudain, le temps d'arriver.
            </p>
            <p>
              J'ai longtemps été éducatrice spécialisée. J'y ai appris l'importance de l'écoute, de la rencontre, de ce que l'on choisit de déposer lorsqu'un espace suffisamment doux nous est offert.
            </p>
            <p className="font-serif italic text-[#2C2621]">Les RDV de l'écriture sont nés de tout cela.</p>
            <p>
              De l'envie de créer des espaces où l'on puisse écrire sans avoir à être écrivain. Sans chercher la bonne phrase. Sans chercher à faire joli. Sans avoir besoin d'être particulièrement inspiré.
            </p>
            <p>Simplement écrire. Un rendez-vous avec soi. Avec son imagination, ses souvenirs, ce que l'on ressent, ce que l'on n'avait encore jamais formulé.</p>
            <p>
              J'ai imaginé Les RDV de l'écriture comme une petite invitation à ralentir dans un monde qui nous demande souvent d'aller vite. Une façon de remettre les mots dans nos vies. Parfois sur une page. Parfois dans une enveloppe. Parfois à plusieurs autour d'une table.
            </p>
            <p className="font-serif italic text-[#2C2621]">
              Mais toujours avec cette même envie&nbsp;: faire une place aux mots, et à ce qu'ils peuvent ouvrir en nous.
            </p>
            <p className="font-hand text-2xl text-[#A3876A] pt-2">Bienvenue aux RDV de l'écriture.</p>
          </div>
          <Link to="/a-propos" data-testid="cta-about" className="btn-outline mt-8 inline-flex">Mieux me connaître</Link>
        </div>
      </Reveal>

      {/* ── 9. CLUB DE LETTRES ─────────────────────────────────────────── */}
      <Reveal as="section" className="bg-[#2C2621] text-[#FDFBF7] py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-10 md:gap-12 items-center">
          <div className="md:col-span-5 order-2 md:order-1 relative">
            <div className="photo-print -rotate-2">
              <img src="/club-enveloppe-cire.jpg"
                alt="Enveloppe kraft du Club de Lettres scellée à la cire, carte nuages et feuilles d'automne"
                className="w-full h-[320px] md:h-[500px] object-cover"/>
            </div>
            <span className="sticker sticker-cream absolute -right-2 bottom-8 rotate-3 font-hand text-xl">Scellée à la cire, pour toi</span>
          </div>
          <div className="md:col-span-7 order-1 md:order-2">
            <p className="font-script text-5xl md:text-6xl text-[#E9B9A4] leading-none mb-4">Le Club de Lettres</p>
            <h2 className="font-serif text-2xl md:text-4xl leading-tight mb-5 md:mb-6">
              Et si, chaque mois, une enveloppe venait t'inviter à ralentir&nbsp;?
            </h2>
            <p className="text-base md:text-lg text-[#EDE4D6] leading-relaxed mb-6">
              Une lettre, une inspiration de la nature, une carte à garder, une invitation à écrire à ton tour et une petite attention — tout ça dans ta boîte aux lettres, chaque mois. <strong className="text-[#FDFBF7]">Dès 12 € par mois</strong>, sans engagement, ou en formule 3, 6 et 12 mois.
            </p>
            <ul className="flex flex-wrap gap-x-3 gap-y-3 mb-8">
              {["Une lettre", "Une inspiration nature", "Une carte", "Une invitation à écrire", "Une petite attention"].map((l, i) => (
                <li key={l} className={`sticker ${i % 2 ? "sticker-terra" : "sticker-cream"} ${["-rotate-2", "rotate-1", "-rotate-1", "rotate-2", "-rotate-1"][i]} font-hand text-lg md:text-xl`}>{l}</li>
              ))}
            </ul>
            <div className="flex flex-wrap items-center gap-4">
              <Link to="/club-de-lettres" data-testid="cta-mailclub-home" className="inline-flex items-center gap-2 px-7 py-3.5 bg-[#C35A3E] text-[#FDFBF7] hover:bg-[#A94A31] transition-colors duration-300">
                <MailOpen size={16}/> Découvrir le Club de Lettres
              </Link>
              <Link to="/club-de-lettres/offrir" data-testid="cta-mailclub-gift-home" className="inline-flex items-center gap-2 font-hand text-xl text-[#E9B9A4] hover:text-[#FDFBF7] transition-colors duration-300">
                <Gift size={16}/> Envie de l'offrir ? Formules 3, 6 et 12 mois →
              </Link>
            </div>
          </div>
        </div>
      </Reveal>

      {/* ── 10. RÉASSURANCE / FAQ ──────────────────────────────────────── */}
      <Reveal as="section" className="max-w-5xl mx-auto px-6 md:px-12 py-16 md:py-24">
        <div className="max-w-2xl mb-10 md:mb-14">
          <p className="font-hand text-xl md:text-2xl text-[#A3876A] mb-3">Avant de te lancer</p>
          <h2 className="font-serif text-2xl md:text-4xl text-[#2C2621] leading-tight">
            Quelques petites <em className="italic">réponses</em>.
          </h2>
        </div>
        <div className="space-y-3 md:space-y-4">
          {reassurances.map((r, i) => (
            <details key={r.q} data-testid={`reassure-${i}`}
              className="group bg-[#FDFBF7] border border-[#E8DCC8] open:border-[#A3876A] transition-colors">
              <summary className="cursor-pointer list-none px-5 md:px-6 py-4 md:py-5 flex items-center justify-between gap-4 font-serif text-base md:text-lg text-[#2C2621]">
                <span>{r.q}</span>
                <span className="font-hand text-2xl text-[#A3876A] transition-transform group-open:rotate-45" aria-hidden="true">+</span>
              </summary>
              <p className="px-5 md:px-6 pb-5 md:pb-6 text-[#4A4036] leading-relaxed text-sm md:text-base border-t border-[#E8DCC8] pt-4">{r.a}</p>
            </details>
          ))}
        </div>
        <div className="mt-8 text-center">
          <Link to="/faq" data-testid="cta-full-faq" className="text-[#A3876A] hover:text-[#8A7158] font-serif italic text-base underline underline-offset-4 decoration-[#DCCBBA] hover:decoration-[#A3876A]">
            Voir toutes les questions →
          </Link>
        </div>
      </Reveal>

      {/* ── 11. CARTE CADEAU — offrir ──────────────────────────────────── */}
      <Reveal as="section" className="bg-[#F5F0E6] py-16 md:py-24 border-y border-[#E8DCC8]">
        <div className="max-w-6xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-8 md:gap-12 items-center">
          <div className="md:col-span-7">
            <p className="font-hand text-xl md:text-2xl text-[#A3876A] mb-4">Offrir un rendez-vous</p>
            <h2 className="font-serif text-2xl md:text-4xl lg:text-5xl text-[#2C2621] leading-tight mb-5 md:mb-6">
              Et si tu offrais <em className="italic">du temps, des mots</em>, une attention particulière ?
            </h2>
            <p className="text-base md:text-lg text-[#4A4036] leading-relaxed mb-4">
              Un anniversaire, un mariage, une naissance, Noël, ou tout simplement parce que tu penses à cette personne — la carte cadeau des RDV de l'écriture est un présent qui ne ressemble à aucun autre.
            </p>
            <p className="text-base md:text-lg text-[#4A4036] leading-relaxed mb-6 md:mb-8">
              Elle offre à celle ou celui qui la reçoit une pause, une rencontre avec les mots, un moment rien qu'à soi.
            </p>
            <Link to="/carte-cadeau" data-testid="cta-gift" className="btn-primary">
              <Gift size={16}/> Je veux offrir un rendez-vous
            </Link>
          </div>
          <div className="md:col-span-5">
            <img src={IMG_GIFT} alt="Enveloppe kraft et fleurs séchées" className="w-full h-[280px] md:h-[420px] object-cover"/>
          </div>
        </div>
      </Reveal>

      {/* ── 12. CTA FINAL ──────────────────────────────────────────────── */}
      <Reveal as="section" className="py-16 md:py-32">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <p className="font-hand text-xl md:text-2xl text-[#A3876A] mb-4 md:mb-6">Et si c'était maintenant</p>
          <h2 className="font-serif text-2xl md:text-5xl text-[#2C2621] leading-tight mb-6 md:mb-8">
            Alors, quel rendez-vous choisiras-tu <em className="italic">avec les mots</em>&nbsp;?
          </h2>
          <p className="text-base md:text-lg text-[#4A4036] leading-relaxed italic mb-10 max-w-2xl mx-auto">
            Un carnet, une enveloppe, un atelier, une simple invitation le lundi matin. Une petite place, quelque part, pour ce que tu as envie de déposer.
          </p>
          <div className="flex flex-wrap justify-center gap-3 md:gap-4">
            <Link to="/programme" data-testid="cta-final-programme" className="btn-primary">
              <Sparkles size={16}/> Découvrir les programmes
            </Link>
            <Link to="/#offres" data-testid="cta-final-offres" className="btn-outline">
              Voir toutes les offres
            </Link>
          </div>
        </div>
      </Reveal>
    </div>
  );
}
